import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { PageShell } from '../../components';
import ProviderForm from './components/ProviderForm';
import { providerService, Provider } from '../../services/providerService';
import { ProviderDocument } from '../../types/provider';
import { useSnackbar } from 'notistack';
import { mensagemDeFalha } from '../../utils/acessoNegado';

const ProviderEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [provider, setProvider] = useState<Provider | null>(null);
  const [providerDocuments, setProviderDocuments] = useState<ProviderDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchProvider = async () => {
      if (!id) {
        setError('ID do prestador não fornecido.');
        setLoading(false);
        return;
      }
      try {
        const fetchedProvider = await providerService.getProviderById(id);
        setProvider(fetchedProvider);

        // Load documents
        try {
          const docData = await providerService.getDocuments(id);
          if (docData.responsibleName) fetchedProvider.responsibleName = docData.responsibleName;
          if (docData.responsibleCRM) fetchedProvider.responsibleCRM = docData.responsibleCRM;
          setProviderDocuments(docData.documents || []);
        } catch {
          // Documents not found is ok
        }
      } catch (err: unknown) {
        const apiError = err as { response?: { data?: { message?: string } }; message?: string };
        const errorMessage = (apiError.response?.data?.message || apiError.message) ?? 'Erro ao carregar dados do prestador';
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchProvider();
  }, [id, enqueueSnackbar]);

  const handleSubmit = async (data: any) => {
    if (!id) return;
    setIsSubmitting(true);
    try {
      const { documents, pendingFiles: _pendingFiles, ...providerData } = data;
      await providerService.updateProvider(id, providerData);

      // Save documents (number/validity)
      if (documents && documents.length > 0) {
        try {
          await providerService.saveDocuments(id, {
            responsibleName: providerData.responsibleName,
            responsibleCRM: providerData.responsibleCRM,
            documents,
          });
        } catch {
          // non-critical
        }
      } else {
        // Still save responsible fields even without documents
        try {
          await providerService.saveDocuments(id, {
            responsibleName: providerData.responsibleName,
            responsibleCRM: providerData.responsibleCRM,
            documents: [],
          });
        } catch {
          // non-critical
        }
      }

      enqueueSnackbar('Prestador atualizado com sucesso!', { variant: 'success' });
      navigate('/providers');
    } catch (error: unknown) {
      const errorMessage = mensagemDeFalha(error, 'Erro ao atualizar prestador');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/providers');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Quem clicou numa linha com nome e cidade precisa reconhecer o registro que abriu; um número
  // de identificação não confirma nada.
  const nomeDoPrestador = provider ? provider.tradeName || provider.legalName : null;
  const titulo = nomeDoPrestador ?? `Editar Prestador #${id}`;

  const breadcrumbs = [
    { label: 'Início', path: '/home' },
    { label: 'Prestadores', path: '/providers' },
    { label: nomeDoPrestador ?? 'Editar' },
  ];

  if (error) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title={titulo}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <Button variant="contained" onClick={() => navigate('/providers')}>
          Voltar para a lista
        </Button>
      </PageShell>
    );
  }

  if (!provider) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title={titulo}>
        <Typography variant="h6">
          Prestador não encontrado.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/providers')}>
          Voltar para a lista
        </Button>
      </PageShell>
    );
  }

  return (
    <PageShell breadcrumbs={breadcrumbs} title={titulo}>
      <ProviderForm initialData={provider} providerId={id} initialDocuments={providerDocuments} onSubmit={handleSubmit} onCancel={handleCancel} isSubmitting={isSubmitting} />
    </PageShell>
  );
};

export default ProviderEditPage;
